import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const DEFAULT_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
const DEFAULT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface FileUploadProps {
  currentImageUrl?: string | null;
  helperText?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  showLabel?: boolean;
  /** Default: jpeg, png, webp. Use ["image/jpeg", "image/png"] for JPG/PNG only. */
  acceptedMimeTypes?: string[];
  /** Default 5 MB (profile images). Use 10 MB for accountant certificate PDFs. */
  maxFileSizeBytes?: number;
  /** `document`: PDF-style row (icon + filename); `image`: avatar preview (default). */
  variant?: "image" | "document";
  onFileChange: (file: File | null) => void;
}

export function FileUpload({
  currentImageUrl,
  helperText,
  disabled,
  error,
  label,
  showLabel = true,
  acceptedMimeTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
  variant = "image",
  onFileChange,
}: FileUploadProps): JSX.Element {
  const { t } = useTranslation();
  const isPdfOnly =
    acceptedMimeTypes.length === 1 && acceptedMimeTypes[0] === "application/pdf";
  const hasWebP = acceptedMimeTypes.includes("image/webp");
  const acceptHint = helperText?.trim()
    ? helperText
    : isPdfOnly
      ? t("components.fileUpload.acceptHintPdf", {
          maxMb: Math.round(maxFileSizeBytes / (1024 * 1024)),
        })
      : hasWebP
        ? t("components.fileUpload.acceptHintJpgPngWebp")
        : t("components.fileUpload.acceptHintJpgPng");
  const displayLabel = label ?? t("components.fileUpload.defaultLabel");
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImageUrl ?? null
  );
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [hasCustomFile, setHasCustomFile] = useState(false);

  useEffect(() => {
    if (!hasCustomFile) {
      setPreviewUrl(currentImageUrl ?? null);
    }
  }, [currentImageUrl, hasCustomFile]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const clearBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const handleFiles = useCallback(
    (file: File | null) => {
      if (!file) {
        clearBlobUrl();
        setHasCustomFile(false);
        setSelectedFileName(null);
        setValidationError(null);
        setPreviewUrl(currentImageUrl ?? null);
        onFileChange(null);
        return;
      }
      if (!acceptedMimeTypes.includes(file.type)) {
        setValidationError(
          isPdfOnly
            ? t("components.fileUpload.validationOnlyPdf")
            : acceptedMimeTypes.length === 2
              ? t("components.fileUpload.validationOnlyJpgPng")
              : t("components.fileUpload.validationOnlyJpgPngWebp")
        );
        setSelectedFileName(null);
        clearBlobUrl();
        setHasCustomFile(false);
        setPreviewUrl(currentImageUrl ?? null);
        onFileChange(null);
        return;
      }
      if (file.size > maxFileSizeBytes) {
        setValidationError(
          t("components.fileUpload.validationMaxSizeMb", {
            maxMb: Math.round(maxFileSizeBytes / (1024 * 1024)),
          })
        );
        setSelectedFileName(null);
        clearBlobUrl();
        setHasCustomFile(false);
        setPreviewUrl(currentImageUrl ?? null);
        onFileChange(null);
        return;
      }
      setValidationError(null);
      clearBlobUrl();
      setSelectedFileName(file.name);
      if (variant === "document") {
        setPreviewUrl(null);
        setHasCustomFile(true);
        onFileChange(file);
        return;
      }
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
      setHasCustomFile(true);
      onFileChange(file);
    },
    [
      clearBlobUrl,
      currentImageUrl,
      acceptedMimeTypes,
      isPdfOnly,
      maxFileSizeBytes,
      onFileChange,
      t,
      variant,
    ]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      handleFiles(file);
    },
    [handleFiles]
  );

  const triggerInput = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const shouldRenderLabel = showLabel && displayLabel;
  const showDocumentChosen = variant === "document" && hasCustomFile && selectedFileName;

  return (
    <div className="space-y-2">
      {shouldRenderLabel && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{displayLabel}</Label>
          {helperText != null &&
          helperText.trim() !== "" &&
          variant === "image" ? (
            <p className="text-xs text-muted-foreground">{helperText}</p>
          ) : null}
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={triggerInput}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            triggerInput();
          }
        }}
        className={cn(
          "flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-4 transition-colors hover:border-primary hover:bg-muted/60 sm:flex-row sm:items-center",
          disabled && "cursor-not-allowed opacity-70"
        )}
      >
        {variant === "document" ? (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50 sm:h-14 sm:w-14">
            <FileText className="h-8 w-8 text-muted-foreground sm:h-7 sm:w-7" aria-hidden />
          </div>
        ) : (
          <Avatar className="h-20 w-20 shrink-0 sm:h-14 sm:w-14">
            {previewUrl ? (
              <AvatarImage src={previewUrl} alt="Preview" />
            ) : (
              <AvatarFallback>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("components.fileUpload.image")}
                </span>
              </AvatarFallback>
            )}
          </Avatar>
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="truncate text-sm font-medium text-foreground">
            {variant === "document"
              ? showDocumentChosen
                ? selectedFileName
                : t("components.fileUpload.uploadDocument")
              : previewUrl
                ? t("components.fileUpload.changeImage")
                : t("components.fileUpload.uploadImage")}
          </p>
          <p className="text-xs text-muted-foreground">{acceptHint}</p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedMimeTypes.join(",")}
        className="sr-only"
        disabled={disabled}
        onChange={handleInputChange}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={triggerInput}
          disabled={disabled}
        >
          {variant === "document"
            ? hasCustomFile
              ? t("components.fileUpload.replace")
              : t("components.fileUpload.selectFile")
            : previewUrl != null
              ? t("components.fileUpload.replace")
              : t("components.fileUpload.selectFile")}
        </Button>
      </div>
      {(validationError != null || error != null) && (
        <p className="text-xs text-destructive">
          {validationError ?? error}
        </p>
      )}
    </div>
  );
}
